<template>
    <hox-modal data-testid="edit-campaign-modal" @close="close">
        <hox-loading-layer v-if="isLoadingUpdateCampaign || isLoadingCampaign" :is-full-screen="false" />

        <template #header>Edit campaign</template>

        <hox-alert v-if="hasErrorLoadingCampaign" margin-bottom="none" type="danger">
            <template #title>Uh oh!</template>
            <template #content>
                <p>Something went wrong while getting some data. Try again in a few moments.</p>
            </template>
            <template #actionItems>
                <Button type="primary" @click="refetchCampaign">Retry</Button>
            </template>
        </hox-alert>

        <template v-else>
            <CampaignForm
                v-if="initialFormData"
                ref="form"
                :initial-form-data="initialFormData"
                :sections="sections"
                :upload-config="uploadConfig"
                :disable-wpp-project-id="isWppLogin"
                :has-creative-x-enabled="hasCreativeXEnabled"
                :client-creative-x-secret-id="clientCreativeXSecretId"
                :has-platform-metadata="hasPlatformMetadata"
                :disable-campaign-type="!!campaign?.hasContent && !!campaign?.campaignType"
                @hasValidationErrorsUpdate="hasValidationErrors = $event"
                @submit="updateCampaign"
            />

            <p v-for="error in mutationErrors" :key="error" class="error-wrapper">Error: {{ error }}</p>
        </template>

        <template #footer>
            <div class="footer-buttons-container">
                <Button
                    v-if="userCanDeleteCampaign"
                    :disabled="hasErrorLoadingCampaign || isLoadingUpdateCampaign"
                    ghost
                    type="error"
                    data-testid="edit-campaign-modal__delete-button"
                    @click="toggleDeleteCampaignModalIsVisible"
                >
                    Delete
                </Button>

                <Button
                    type="primary"
                    :disabled="hasValidationErrors"
                    data-testid="edit-campaign-modal__save-button"
                    @click="submit"
                >
                    Save
                </Button>
            </div>
        </template>

        <DeleteCampaignModal
            v-if="deleteCampaignModalIsVisible && campaign"
            :campaign-id="campaign._id"
            :campaign-name="campaign.name"
            @close="toggleDeleteCampaignModalIsVisible"
            @campaignDeleted="deleted"
        />
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";

import { USER_ACTIONS } from "shared-utils/enums/user";
import { DISTRIBUTION_CHANNEL } from "shared-utils/enums/client";

import type { ApolloError } from "@apollo/client/core";
import type { Campaign, CampaignSection, EditableGroup, MastheadUploadConfig } from "@/types/types";

import CampaignForm, { type CampaignFormData } from "@/components/CampaignForm.vue";
import DeleteCampaignModal from "@/components/DeleteCampaignModal.vue";

import updateCampaignMutation from "@/apollo/mutations/UpdateCampaign.gql";
import campaignQuery from "@/apollo/queries/v2/Campaign.gql";
import { AuthGetters } from "@/store/modules/auth";
import getDefaultCreativeXCampaignSettings from "@/utils/CreativeX/getDefaultCreativeXCampaignSettings";
import { PlatformTypeOption } from "shared-utils/enums/editables";
import { userCan } from "@/services/Auth";

export default defineComponent({
    components: {
        CampaignForm,
        DeleteCampaignModal
    },

    props: {
        campaignId: {
            required: true,
            type: String
        },

        sections: {
            required: true,
            type: Array as PropType<CampaignSection[]>
        },

        hasCreativeXEnabled: {
            type: Boolean,
            default: false
        },

        clientCreativeXSecretId: {
            type: String,
            required: false
        }
    },

    emits: ["close", "deleted", "campaignEdited"],

    data() {
        return {
            initialFormData: null as Partial<CampaignFormData> | null,
            deleteCampaignModalIsVisible: false,
            hasErrorLoadingCampaign: false,
            hasValidationErrors: false,
            isLoadingCampaign: 0,
            isLoadingUpdateCampaign: false,
            hasPlatformMetadata: false,
            mutationErrors: [] as string[],
            uploadConfig: undefined as MastheadUploadConfig | undefined,
            campaign: null as Campaign | null,
            isWppLogin: false
        };
    },

    computed: {
        userCanDeleteCampaign() {
            return userCan(USER_ACTIONS.canRemoveCampaign, {
                campaignId: this.campaignId,
                clientId: this.clientId
            });
        },

        clientId() {
            return this.$route.params.clientId as string;
        },

        canUpdateOpendcPlatformSettings() {
            return this.$store.state.ui.currentClient?.distributionChannel === DISTRIBUTION_CHANNEL.WPP_MEDIA || false;
        }
    },

    mounted() {
        this.isWppLogin = this.$store.getters[AuthGetters.isWppLogin];

        const canEdit = userCan(USER_ACTIONS.canEditCampaign, {
            clientId: this.clientId,
            campaignId: this.campaignId
        });

        if (!canEdit) {
            this.$snackbar.warning("You do not have access to edit campaigns");
            this.close();
        }
    },

    methods: {
        close() {
            this.$emit("close");
        },

        deleted() {
            this.$emit("deleted");
            this.close();
        },

        refetchCampaign() {
            this.$apollo.queries.campaign?.refetch();
        },

        toggleDeleteCampaignModalIsVisible() {
            this.deleteCampaignModalIsVisible = !this.deleteCampaignModalIsVisible;
        },

        submit() {
            (this.$refs.form as HTMLFormElement).submit();
        },

        async updateCampaign(formData: CampaignFormData) {
            if (this.isLoadingUpdateCampaign || this.hasValidationErrors) {
                return;
            }

            this.isLoadingUpdateCampaign = true;
            this.mutationErrors = [];

            try {
                await this.$apollo.mutate({
                    mutation: updateCampaignMutation,
                    variables: {
                        campaignId: this.campaignId,
                        campaign: {
                            name: formData.name,
                            campaignType: formData.campaignType,
                            jiraTicketUrl: formData.jiraTicketUrl,
                            masthead: formData.content,
                            sectionId: formData.sectionId === "none" ? null : formData.sectionId,
                            toneOfVoice: formData.toneOfVoice,
                            defaultTimelineLength: +formData.defaultTimelineLength,
                            wppProjectId: formData.wppProjectId,
                            wppTenantId: this.$store.state.auth?.wppOsContext?.tenant?.id,
                            wppAzId: this.$store.state.auth?.wppOsContext?.hierarchy?.azId,
                            ...(this.hasCreativeXEnabled && {
                                creativeXPlatformSettings: {
                                    ...getDefaultCreativeXCampaignSettings(),
                                    ...formData.creativeXPlatformSettings
                                }
                            }),
                            opendcPlatformSettings: this.canUpdateOpendcPlatformSettings
                                ? formData.opendcPlatformSettings
                                : null
                        }
                    }
                });

                this.$snackbar.success("Campaign updated");
                this.$emit("campaignEdited");
                this.close();
            } catch (error) {
                const graphQLErrors = (error as ApolloError)?.graphQLErrors || [];
                this.mutationErrors = graphQLErrors.map(({ message }) => message);
            } finally {
                this.isLoadingUpdateCampaign = false;
            }
        }
    },

    apollo: {
        campaign: {
            query: campaignQuery,
            fetchPolicy: "network-only",
            loadingKey: "isLoadingCampaign",

            variables() {
                return {
                    campaignId: this.campaignId
                };
            },

            result({ data: { campaign, editableGroupsByCampaign }, error }) {
                if (error) {
                    this.hasErrorLoadingCampaign = true;
                    return;
                }

                this.campaign = campaign;
                this.hasErrorLoadingCampaign = false;
                this.uploadConfig = campaign.mastheadUploadConfig;

                this.hasPlatformMetadata = !!editableGroupsByCampaign.find(
                    (group: EditableGroup) =>
                        PlatformTypeOption.facebookPlatform === group.name ||
                        PlatformTypeOption.platformMetadata === group.name
                );

                this.initialFormData = {
                    mastheadImageUrl: campaign.mastheadImageUrl,
                    name: campaign.name,
                    campaignType: campaign.campaignType,
                    jiraTicketUrl: campaign.jiraTicketUrl,
                    sectionId: campaign.section?._id,
                    toneOfVoice: campaign.toneOfVoice,
                    defaultTimelineLength: campaign.defaultTimelineLength,
                    wppProjectId: campaign.wppProjectId,
                    opendcPlatformSettings: campaign.opendcPlatformSettings,
                    ...(this.hasCreativeXEnabled && {
                        creativeXPlatformSettings: {
                            ...getDefaultCreativeXCampaignSettings(),
                            ...campaign.creativeXPlatformSettings
                        }
                    })
                };
            }
        }
    }
});
</script>

<style scoped lang="css">
.error-wrapper {
    color: red;
    margin-top: 12px;
}

.footer-buttons-container {
    display: flex;
    justify-content: space-between;
    width: 100%;
}
</style>
